import {
  DPR,
  UI_DEPTH,
  MAX_WIDTH,
  LOCAL_STORAGE_KEYS,
} from '@constants/common.const';
import { UiEventType } from '@enums/game-events.enum';
import { ActionType } from '@enums/node-types.enum';
import {
  emitIframeMessage,
  IframeMessage,
} from '@longnecktech/splash-commons-fe';
import { ActionInfo } from '@models/game-config.interface';
import { Collectible } from '@models/collectibles';
import { GoalsData } from '@models/goals';
import { MilestonesData } from '@models/milestones';
import { UserPrize } from '@models/prize';
import { RankData } from '@models/rank';
import { Rules } from '@models/rules';
import { SceneKey } from '@models/scene-keys';
import { UserHistory } from '@models/user-history';
import { ApiService } from '@services/api/ApiService';
import { GameConfigService } from '@services/core/GameConfigService';
import GameState from '@services/core/GameState';
import { LocalStorageService } from '@services/core/LocalStorageService';
import { TranslationService } from '@services/core/TranslationService';
import { UIService } from '@services/game/UIService';
import { FooterBar, FooterAction } from '../ui/FooterBar';
import { GoalsSheet } from '../ui/GoalsSheet';
import { HeaderBar } from '../ui/HeaderBar';
import { ModalSheet } from '../ui/modal-sheet';
import { MilestonesSheet } from '../ui/MilestonesSheet';
import { PopupStack } from '../ui/PopupStack';
import { RankSheet } from '../ui/RankSheet';
import { RulesSheet } from '../ui/RulesSheet';
import { HistorySheet } from '../ui/HistorySheet';
import { TooltipStack, TooltipStackEntry } from '../ui/TooltipStack';

const DELAY_BEFORE_SUCCESS_DIALOG = 2500; // ms

export default class UIScene extends Phaser.Scene {
  private uiService!: UIService;
  private api!: ApiService;
  private translationService!: TranslationService;
  // Layout constants
  private readonly margin = 16 * DPR;
  private readonly headerHeight = 56 * DPR;
  private readonly footerHeight = 52 * DPR;
  private readonly maxUIWidth = MAX_WIDTH * DPR;
  private readonly radius = 16 * DPR;
  private footer!: FooterBar;
  private header!: HeaderBar;
  private rulesSheet?: RulesSheet<Rules>;
  private historySheet?: HistorySheet<UserHistory>;
  private milestonesSheet?: MilestonesSheet<MilestonesData>;
  private rankSheet?: RankSheet<RankData>;
  private goalsSheet?: GoalsSheet<GoalsData>;
  private gameState!: GameState;
  private configService!: GameConfigService;
  private localStorageService!: LocalStorageService;
  private tooltipStack?: TooltipStack;
  private popupStack?: PopupStack;
  private countdownEntry?: TooltipStackEntry;
  private countdownTimer?: Phaser.Time.TimerEvent;

  // Event handler references for cleanup
  private xpGainedHandler!: (data: { uuid: string; message: string }) => void;
  private itemGainedHandler!: (data: {
    id: string;
    type: string;
    qty: number;
  }) => void;
  private rollsChangedHandler!: (ableToRoll: boolean) => void;
  private rewardGainedHandler!: (data: {
    symbolKey: string;
    label: string;
  }) => void;
  private collectibleGainedHandler!: (data: {
    collectibles: Collectible[];
    uuid: string;
    message: string;
  }) => void;
  private collectibleGainedFullHandler!: (data: {
    collectibles: Collectible[];
    collectible: Collectible;
    message: string;
  }) => void;
  private completeMilestoneHandler!: (data: { action: ActionInfo }) => void;
  private claimInstantPrizeHandler!: (data: { action: ActionInfo }) => void;
  private popupRequestedHandler!: (data: {
    text: string;
    imagePath?: string;
  }) => void;

  constructor() {
    super(SceneKey.UI);
  }

  preload() {
    this.load.html('rules', 'assets/views/rules.html');
    this.load.html('history', 'assets/views/history.html');
    this.load.html('milestones', 'assets/views/milestones.html');
    this.load.html('leaderboard', 'assets/views/leaderboard.html');
    this.load.html('goals', 'assets/views/goals.html');
  }

  create() {
    this.configService = new GameConfigService();
    this.gameState = new GameState();
    this.uiService = new UIService(this);
    this.translationService = new TranslationService();
    this.api = new ApiService();
    this.localStorageService = new LocalStorageService();

    this.createHeader();
    this.createFooter();
    this.createPopup();
    this.createPopupStack();

    // Setup event listeners for UI changes
    this.setupEventListeners();

    // Initial dice button state
    this.updateDiceButtonState();
    this.showCompletedGoalNotifications();

    // keep UI above game
    this.scene.bringToTop();

    // Handle resize
    this.scale.on('resize', this.handleResize, this);
  }

  shutdown() {
    this.stopCountdown();

    // Remove all event listeners registered in setupEventListeners
    this.gameState.off(
      UiEventType.ROLLS_CHANGED,
      this.rollsChangedHandler,
      this,
    );
    this.gameState.off(UiEventType.XP_GAINED, this.xpGainedHandler, this);
    this.gameState.off(UiEventType.ITEM_GAINED, this.itemGainedHandler, this);
    this.gameState.off(
      UiEventType.REWARD_GAINED,
      this.rewardGainedHandler,
      this,
    );
    this.gameState.off(
      UiEventType.COLLECTIBLE_GAINED,
      this.collectibleGainedHandler,
      this,
    );
    this.gameState.off(
      UiEventType.COLLECTIBLE_GAINED_FULL,
      this.collectibleGainedFullHandler,
      this,
    );
    this.gameState.off(
      UiEventType.POPUP_REQUESTED,
      this.popupRequestedHandler,
      this,
    );
    this.gameState.off(
      UiEventType.COMPLETE_MILESTONE,
      this.completeMilestoneHandler,
      this,
    );
    this.gameState.off(
      UiEventType.CLAIM_INSTANT_PRIZE,
      this.claimInstantPrizeHandler,
      this,
    );

    this.popupStack?.destroy();
    this.popupStack = undefined;

    // Remove resize listener
    this.scale.off('resize', this.handleResize, this);
  }

  // --- Layout helpers -------------------------------------------------------
  private get uiWidth(): number {
    const w = this.scale.width;
    const available = Math.max(0, w - this.margin * 2);
    return Math.min(this.maxUIWidth, available);
  }

  private centerX(): number {
    return this.scale.width / 2;
  }

  /** Y coordinate (in scene space) that is 16px above the footer bar. */
  private tooltipAboveFooterY(): number {
    // Footer is positioned by its center Y.
    const footerCenterY = this.scale.height - this.footerHeight - this.margin;
    const footerTopY = footerCenterY - this.footerHeight / 2;
    return footerTopY - 8 * DPR;
  }

  private handleResize(gameSize: Phaser.Structs.Size) {
    const uiWidth = this.uiWidth;

    // Header
    if (this.header) {
      this.header.setPosition(this.centerX(), this.margin);
      this.header.rebuild(uiWidth, this.headerHeight);
    }

    // Footer
    if (this.footer) {
      this.footer.setPosition(
        this.centerX(),
        gameSize.height - this.footerHeight - this.margin,
      );
      this.footer.redraw(uiWidth, this.footerHeight);
      this.footer.layoutButtons();
    }
  }

  private createHeader() {
    this.header = new HeaderBar({
      scene: this,
      x: this.centerX(),
      y: this.margin,
      width: this.uiWidth,
      height: this.headerHeight,
      radius: this.radius,
      collectibles: this.gameState.collectibles,
      onBack: () => {
        this.scene.stop(SceneKey.UI);
        this.scene.stop(SceneKey.Game);
        this.scene.start(SceneKey.Home);
      },
    });
    this.header.setDepth(UI_DEPTH);
  }

  private get historyBaseY(): number {
    return this.margin + this.headerHeight + 16 * DPR;
  }

  private createFooter() {
    this.footer = new FooterBar({
      scene: this,
      x: this.centerX(),
      y: this.scale.height - this.footerHeight - this.margin,
      width: this.uiWidth,
      height: this.footerHeight,
      radius: this.radius,
      onAction: (label: FooterAction) => {
        switch (label) {
          case FooterAction.DICE:
            this.toggleDiceScene();
            break;
          case FooterAction.RULES:
            this.togglePopup<RulesSheet<Rules>>(this.rulesSheet);
            break;
          case FooterAction.HISTORY:
            this.togglePopup<HistorySheet<UserHistory>>(this.historySheet);
            break;
          case FooterAction.MILESTONES:
            this.togglePopup<MilestonesSheet<MilestonesData>>(
              this.milestonesSheet,
            );
            break;
          case FooterAction.RANK:
            this.togglePopup<RankSheet<RankData>>(this.rankSheet);
            break;
          case FooterAction.GOALS:
            this.togglePopup<GoalsSheet<GoalsData>>(this.goalsSheet);
            break;
          default:
            break;
        }
      },
    });
    this.footer.setDepth(UI_DEPTH);
    this.footer.setButtonEnabled(
      FooterAction.GOALS,
      this.gameState.goals.length > 0,
    );
  }

  private createPopupStack(): void {
    this.popupStack = new PopupStack({
      scene: this,
      x: this.centerX(),
      y: this.scale.height * 0.3,
    });
  }

  private pushPopup(text: string, imagePath?: string): void {
    this.popupStack?.push({ text, imagePath });
  }

  private createPopup() {
    this.createRuleSheet();
    this.createHistorySheet();
    this.createMilestonesSheet();
    this.createRankSheet();
    this.createGoalsSheet();
  }

  private createRuleSheet(): void {
    const data: Partial<Rules> = this.configService.getRulesData();
    this.rulesSheet = new RulesSheet<Rules>(this, {
      templateData: {
        ...data,
        title: this.translationService.translate('rules.title'),
        subtitle: this.translationService.translate('rules.subtitle'),
        winTitle: this.translationService.translate('rules.instantWins'),
        gameSymbolTitle: this.translationService.translate('rules.gameSymbols'),
        collectiblesTitle:
          this.translationService.translate('rules.collectibles'),
      } as Rules,
    });
  }

  private buildHistoryData(): UserHistory {
    const records = [
      ...this.gameState.claimedCollectiblesPrizes,
      ...this.gameState.claimedInstantPrizes,
    ];
    const winsWord = this.translationService.translate(
      records.length === 1 ? 'history.historyWin' : 'history.historyWins',
    );
    return {
      title: this.translationService.translate('history.title'),
      subtitle: this.translationService.translate('history.subtitle'),
      noHistoryText: this.translationService.translate('history.noHistory'),
      historyTitle: records.length
        ? `${this.translationService.translate('history.historyTitle')} ${records.length} ${winsWord}`
        : '',
      records,
    };
  }

  private createHistorySheet(): void {
    this.historySheet = new HistorySheet<UserHistory>(this, {
      templateData: this.buildHistoryData(),
      dataSupplier: () => this.buildHistoryData(),
    });
  }

  private createMilestonesSheet(): void {
    const checkpoints = (this.configService.actions || []).filter(
      (a) => a.actionType === ActionType.Checkpoint,
    );
    const finishes = (this.configService.actions || []).filter(
      (a) => a.actionType === ActionType.Finish,
    );
    this.milestonesSheet = new MilestonesSheet<MilestonesData>(this, {
      templateData: {
        title: this.translationService.translate('milestones.title'),
        subtitle: this.translationService.translate('milestones.subtitle'),
        stageName: this.configService.currentStage?.name ?? '',
        collectiblesTitle: this.translationService.translate(
          'milestones.milestones',
        ),
        noMilestonesText: this.translationService.translate(
          'milestones.noMilestones',
        ),
        milestones: [...checkpoints, ...finishes], // put finishes to the end
      },
    });
  }

  private createGoalsSheet(): void {
    this.goalsSheet = new GoalsSheet<GoalsData>(this, {
      templateData: {
        title: this.translationService.translate('goals.title'),
        subtitle: this.translationService.translate('goals.subtitle'),
        goalsTitle: this.translationService.translate('goals.goals'),
        noGoalsText: 'No goals yet',
        goals: this.gameState.goals,
        gameUuid: this.configService.sessionParams.gameUuid,
      },
    });
  }

  private createRankSheet(): void {
    this.rankSheet = new RankSheet(this, {
      templateData: {
        currentJackpot: this.translationService.translate(
          'leaderboard.currentJackpot',
        ),
        title: this.translationService.translate('leaderboard.title'),
        closesOn: this.translationService.translate('leaderboard.closesOn'),
        searchPlaceholder: this.translationService.translate(
          'leaderboard.searchPlaceholder',
        ),
        asItStands: this.translationService.translate('leaderboard.asItStands'),
        you: this.translationService.translate('leaderboard.you'),
        ptsSuffix: this.translationService.translate('leaderboard.ptsSuffix'),
        cashCredit: this.translationService.translate('leaderboard.cashCredit'),
      },
    });
  }

  private togglePopup<T extends ModalSheet<unknown>>(
    modal: T | undefined,
  ): void {
    if (!modal) return;
    modal.showCollapsed();
  }

  private toggleDiceScene() {
    // Prevent opening dice overlay if no rolls remain
    if (!this.gameState.isAbleToRoll()) {
      console.log('UIScene: Cannot roll, no rolls remaining');
      return;
    }

    if (this.scene.isActive('DiceOverlay')) {
      this.scene.stop('DiceOverlay');
    }
    this.footer.setDiceEnabled(false);
    this.scene.launch('DiceOverlay');
    this.scene.bringToTop('DiceOverlay');
  }

  private startCountdownTooltip(seconds: number): void {
    this.stopCountdown();

    this.tooltipStack = new TooltipStack({
      scene: this,
      x: this.centerX(),
      y: this.tooltipAboveFooterY(),
      caretSide: 'bottom',
      width: 160,
    });

    if (seconds > 0) {
      let remaining = seconds;
      this.countdownEntry = this.tooltipStack.push({
        text: this.formatCountdown(remaining),
      });

      this.countdownTimer = this.time.addEvent({
        delay: 1000,
        repeat: seconds - 1,
        callback: () => {
          remaining -= 1;
          if (remaining <= 0) {
            this.stopCountdown();
            this.pollGameState();
            return;
          }
          this.countdownEntry?.setText(this.formatCountdown(remaining));
        },
      });
    } else {
      this.tooltipStack.push({
        text: 'Roll is unavailable now.',
        autoHideDelayMs: 5000,
      });
    }
  }

  private stopCountdown(): void {
    if (this.countdownTimer) {
      this.countdownTimer.destroy();
      this.countdownTimer = undefined;
    }
    this.countdownEntry = undefined;
    this.tooltipStack?.destroy();
    this.tooltipStack = undefined;
  }

  private formatCountdown(totalSeconds: number): string {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    const mm = String(m).padStart(2, '0');
    const ss = String(s).padStart(2, '0');
    const time = h > 0 ? `${h}H:${mm}M:${ss}S` : `${mm}M:${ss}S`;
    return `Next free roll in ${time}`;
  }

  private async pollGameState(): Promise<void> {
    try {
      const state = await this.api.fetchGameState(
        this.configService.sessionParams.gameUuid,
      );
      if (state.rollsAvailable > 0) {
        this.gameState.setRollInfo(state.nextRoll, state.rollsAvailable);
      } else {
        this.uiService.showTooltip({
          scene: this,
          x: this.centerX(),
          y: this.tooltipAboveFooterY(),
          closeOnClick: true,
          text: 'Roll is unavailable now.',
        });
      }
    } catch {
      console.error('Failed to fetch game state');
    }
  }

  private updateDiceButtonState() {
    const canRoll = this.gameState.isAbleToRoll();
    this.footer.setDiceEnabled(canRoll);
    if (!canRoll) {
      this.startCountdownTooltip(this.gameState.getNextRollTime());

      const uncompletedGoals = this.gameState.goals.filter((g) => !g.completed);
      if (uncompletedGoals.length > 0) {
        // add timeout to imitate delay between tooltip appearing,
        // to make it visually nice
        this.time.delayedCall(500, () => {
          this.tooltipStack?.push({
            text: uncompletedGoals[0].ctaMessage,
            autoHideDelayMs: false,
          });
        });
      }
    }
  }

  private showCompletedGoalNotifications(): void {
    const shownMap =
      this.localStorageService.getItem<Record<string, true>>(
        LOCAL_STORAGE_KEYS.GOAL_NOTIFICATIONS_SHOWN,
      ) ?? {};

    const unseenCompleted = this.gameState.goals.filter(
      (g) => g.completed && !shownMap[g.uuid],
    );

    if (unseenCompleted.length === 0) return;

    if (!this.tooltipStack) {
      this.tooltipStack = new TooltipStack({
        scene: this,
        x: this.centerX(),
        y: this.tooltipAboveFooterY(),
        caretSide: 'bottom',
        width: 160,
      });
    }

    const updatedMap = { ...shownMap };
    for (const goal of unseenCompleted) {
      this.tooltipStack.push({
        text: goal.successMessage,
        autoHideDelayMs: 5000,
        closeOnClick: true,
      });
      updatedMap[goal.uuid] = true;
    }

    this.localStorageService.setItem(
      LOCAL_STORAGE_KEYS.GOAL_NOTIFICATIONS_SHOWN,
      updatedMap,
    );
  }

  private setupEventListeners() {
    this.rollsChangedHandler = (ableToRoll: boolean) => {
      if (ableToRoll) {
        this.stopCountdown();
        this.footer.setDiceEnabled(true);
      } else {
        this.updateDiceButtonState();
      }
    };
    this.xpGainedHandler = (data: { uuid: string; message: string }) => {
      this.pushPopup(data.message, data.uuid);
    };

    this.itemGainedHandler = (data: {
      id: string;
      type: string;
      qty: number;
    }) => {
      this.pushPopup(`+${data.qty} ${data.type}`, 'dice');
    };

    this.rewardGainedHandler = (data: { symbolKey: string; label: string }) => {
      this.pushPopup(data.label, data.symbolKey);
    };

    this.collectibleGainedHandler = (data: {
      collectibles: Collectible[];
      uuid: string;
      message: string;
    }) => {
      this.pushPopup(data.message, data.uuid);
      this.time.delayedCall(DELAY_BEFORE_SUCCESS_DIALOG, () => {
        this.header.updateCollectibles(data.collectibles, data.uuid);
      });
    };

    this.collectibleGainedFullHandler = (data: {
      collectibles: Collectible[];
      collectible: Collectible;
      message: string;
    }) => {
      this.collectibleGainedHandler({
        collectibles: data.collectibles,
        uuid: data.collectible.uuid,
        message: data.message,
      });
      this.time.delayedCall(DELAY_BEFORE_SUCCESS_DIALOG, () => {
        this.scene.launch(SceneKey.Reward, {
          title: data.collectible.name,
          subtitle: this.translationService.translate('game.collectible'),
          iconKey: data.collectible.uuid,
          rewardLabel: data.collectible.prize.label,
          ctaLabel: this.translationService.translate('game.collectibleCTA'),
          onCTAClick: () => {
            if (data.collectible.prize) {
              this.claimPrize(data.collectible.prize);
            }
          },
          onCloseClick: () => {
            void this.updateInventory();
            void this.updateEconomy();
            // close the scene
            this.scene.stop(SceneKey.Reward);
          },
        });
      });
    };

    this.completeMilestoneHandler = (data: { action: ActionInfo }) => {
      this.time.delayedCall(DELAY_BEFORE_SUCCESS_DIALOG, () => {
        this.scene.launch(SceneKey.Reward, {
          title: data.action.name,
          subtitle: this.translationService.translate('game.milestone'),
          iconKey: data.action.uuid,
          rewardLabel: data.action.prize?.label ?? data.action.prizeLabel ?? '',
          ctaLabel: this.translationService.translate('game.milestoneCTA'),
          onCTAClick: () => {
            if (data.action.prize) {
              this.claimPrize(data.action.prize);
            }
          },
          onCloseClick: () => {
            void this.updateEconomy();
            // close the scene
            this.scene.stop(SceneKey.Reward);
          },
        });
      });
    };

    this.claimInstantPrizeHandler = (data: { action: ActionInfo }) => {
      // Let the board reward animation play before showing the overlay
      this.time.delayedCall(DELAY_BEFORE_SUCCESS_DIALOG, () => {
        this.scene.launch(SceneKey.Reward, {
          title: data.action.name,
          subtitle: this.translationService.translate('game.instantPrize'),
          iconKey: data.action.uuid,
          rewardLabel: data.action.prize?.label ?? data.action.prizeLabel ?? '',
          ctaLabel: this.translationService.translate('game.instantPrizeCTA'),
          onCTAClick: () => {
            if (data.action.prize) {
              this.claimPrize(data.action.prize);
            }
          },
          onCloseClick: () => {
            void this.updateEconomy();
            // close the scene
            this.scene.stop(SceneKey.Reward);
          },
        });
      });
    };

    this.popupRequestedHandler = (data: {
      text: string;
      imagePath?: string;
    }) => {
      this.pushPopup(data.text, data.imagePath);
    };

    this.gameState.on(
      UiEventType.POPUP_REQUESTED,
      this.popupRequestedHandler,
      this,
    );

    this.gameState.on(
      UiEventType.ROLLS_CHANGED,
      this.rollsChangedHandler,
      this,
    );
    this.gameState.on(UiEventType.XP_GAINED, this.xpGainedHandler, this);
    this.gameState.on(UiEventType.ITEM_GAINED, this.itemGainedHandler, this);
    this.gameState.on(
      UiEventType.REWARD_GAINED,
      this.rewardGainedHandler,
      this,
    );
    this.gameState.on(
      UiEventType.COLLECTIBLE_GAINED,
      this.collectibleGainedHandler,
      this,
    );
    this.gameState.on(
      UiEventType.COLLECTIBLE_GAINED_FULL,
      this.collectibleGainedFullHandler,
      this,
    );
    this.gameState.on(
      UiEventType.COMPLETE_MILESTONE,
      this.completeMilestoneHandler,
      this,
    );
    this.gameState.on(
      UiEventType.CLAIM_INSTANT_PRIZE,
      this.claimInstantPrizeHandler,
      this,
    );
  }

  private claimPrize(prize: UserPrize): void {
    const link = prize.claimLink;
    if (link) {
      (window.top || window).location.href = link;
      return;
    }
    emitIframeMessage(IframeMessage.ClaimPrize, {
      gameType: 'BOARD',
      gameUuid: this.configService.gameUuid,
      PrizeType: prize.type,
      slug: prize.slug,
    });
  }

  private async updateEconomy() {
    const economy = await this.api.fetchEconomy();
    this.gameState.syncEconomy(this.configService.actions, economy);
  }

  private async updateInventory() {
    const inventory = await this.api.fetchInventory();
    this.gameState.initCollectibles(
      this.gameState.collectibles,
      inventory.items,
    );
    // update collectibles in the header
    this.header.updateCollectibles(this.gameState.collectibles);
  }
}
